import { isResolvedIntervention } from "../components/patient/Patient-Menu/Interventions/data";

export const normalizeInterventionsPayload = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (raw && Array.isArray(raw.items)) return raw.items;
  if (raw && Array.isArray(raw.data)) return raw.data;
  if (raw && Array.isArray(raw.results)) return raw.results;
  return [];
};

export const isMarkedForPersonalMedicationReview = (intervention) => {
  if (!intervention) return false;
  return Boolean(
    intervention?.interventionAssessment?.interventionAssessmentForm?.interventionDetails
      ?.addToPersonalMedicationReview ??
      intervention?.interventionAssessment?.interventionAssessmentForm?.interventionDetails
        ?.includeInPersonalMedicationReview ??
      intervention?.addToPersonalMedicationReview ??
      intervention?.includeInPersonalMedicationReview
  );
};

export const getInterventionLinkedMrServiceId = (intervention) =>
  intervention?.interventionAssessment?.interventionAssessmentForm?.interventionDetails
    ?.linkedMedicationReviewClinicalServiceId ??
  intervention?.linkedMedicationReviewClinicalServiceId ??
  null;

/**
 * True when the patient has at least one unresolved intervention marked for PMR
 * that is tied to the given medication-review clinical service id (explicit link
 * or created on that service).
 */
export const hasOutstandingPmrInterventionsForMedicationReview = (
  mrClinicalServiceId,
  interventionsList
) => {
  if (mrClinicalServiceId == null || mrClinicalServiceId === "") return false;
  const list = normalizeInterventionsPayload(interventionsList);
  const sid = String(mrClinicalServiceId);
  return list.some((inv) => {
    if (!isMarkedForPersonalMedicationReview(inv)) return false;
    if (isResolvedIntervention(inv)) return false;
    const linkId = getInterventionLinkedMrServiceId(inv);
    if (linkId != null && linkId !== "" && String(linkId) === sid) return true;
    if (inv.clinicalServiceId != null && String(inv.clinicalServiceId) === sid) return true;
    return false;
  });
};

/**
 * True when any intervention created on the given clinical service is not yet resolved.
 * The PMR reflects the whole medication review, so it stays "Under Review" until every
 * intervention on that review is complete — even when opened from one that is done.
 */
export const hasOutstandingInterventionsForService = (
  clinicalServiceId,
  interventionsList
) => {
  if (clinicalServiceId == null || clinicalServiceId === "") return false;
  const sid = String(clinicalServiceId);
  return normalizeInterventionsPayload(interventionsList).some(
    (inv) =>
      inv?.clinicalServiceId != null &&
      String(inv.clinicalServiceId) === sid &&
      !isResolvedIntervention(inv)
  );
};

const normalizeMrSummaryStatus = (status) => String(status || "").toLowerCase();

/** Deleted services are never link targets; cancelled may still appear when PMR interventions are outstanding. */
export const isMedicationReviewHardExcludedForLink = (item) => {
  const s = normalizeMrSummaryStatus(item?.status);
  return s.includes("deleted");
};

export const isMedicationReviewServiceRunning = (item) => {
  const s = normalizeMrSummaryStatus(item?.status);
  return (
    s === "inprogress" ||
    s === "in-progress" ||
    s === "in_progress" ||
    s === "in progress"
  );
};

/** Best display/sort date for a summary row (aligns with fields used on clinical-service payloads). */
export const getMedicationReviewRecordDate = (item) =>
  item?.date ||
  item?.endDateTime ||
  item?.startDateTime ||
  item?.lastModifiedDate ||
  item?.initialAssessment?.lastUpdatedAt ||
  null;

/**
 * Merge `clinicalServicesSmall.medicationReviews` with medical-summary rows by id.
 * When both exist, the small-list row wins on overlapping keys (in-session status).
 */
export const mergeMedicationReviewRowsForLink = (clinicalServicesSmall, medicationReviewResponse) => {
  const byId = new Map();
  const fromSmall = clinicalServicesSmall?.medicationReviews;
  if (Array.isArray(fromSmall)) {
    for (const svc of fromSmall) {
      if (!svc?.id) continue;
      byId.set(String(svc.id), { ...svc });
    }
  }
  const fromSummary = Array.isArray(medicationReviewResponse) ? medicationReviewResponse : [];
  for (const row of fromSummary) {
    if (!row?.id) continue;
    const key = String(row.id);
    if (byId.has(key)) {
      byId.set(key, { ...row, ...byId.get(key) });
    } else {
      byId.set(key, { ...row });
    }
  }
  return Array.from(byId.values());
};

export const sortMedicationReviewLinkOptions = (rows, currentServiceId) => {
  const sid = currentServiceId != null && currentServiceId !== "" ? String(currentServiceId) : null;
  return [...rows].sort((a, b) => {
    const aid = String(a.id);
    const bid = String(b.id);
    const aRun = isMedicationReviewServiceRunning(a);
    const bRun = isMedicationReviewServiceRunning(b);
    const aCurRun = sid && aid === sid && aRun;
    const bCurRun = sid && bid === sid && bRun;
    if (aCurRun !== bCurRun) return aCurRun ? -1 : 1;
    const aCur = sid && aid === sid;
    const bCur = sid && bid === sid;
    if (aCur !== bCur) return aCur ? -1 : 1;
    if (aRun !== bRun) return aRun ? -1 : 1;
    const ad = getMedicationReviewRecordDate(a);
    const bd = getMedicationReviewRecordDate(b);
    const at = ad ? new Date(ad).getTime() : 0;
    const bt = bd ? new Date(bd).getTime() : 0;
    return bt - at;
  });
};

export const personalMedicationReviewTypeLabel = (code) => {
  if (!code) return "Medication Review";
  if (code === "AnnualMedicationReview") return "Annual Medication Review";
  if (code === "FOLLOWUP" || code === "FollowUp" || code === "followup") {
    return "Follow-up Review";
  }
  return String(code);
};

/**
 * Medication review / follow-up services eligible for the intervention “link to MR” dropdown:
 * running, or unfinished with outstanding PMR-marked interventions.
 */
export const getEligibleMedicationReviewsForInterventionLink = ({
  clinicalServicesSmall,
  medicationReviewResponse,
  interventionsFromStore,
  currentServiceId,
}) => {
  const interventionsList = normalizeInterventionsPayload(interventionsFromStore);
  const merged = mergeMedicationReviewRowsForLink(
    clinicalServicesSmall,
    medicationReviewResponse
  );
  const eligible = merged.filter((item) => {
    if (!item?.id || isMedicationReviewHardExcludedForLink(item)) return false;
    const running = isMedicationReviewServiceRunning(item);
    const outstanding = hasOutstandingPmrInterventionsForMedicationReview(
      item.id,
      interventionsList
    );
    return running || outstanding;
  });
  return sortMedicationReviewLinkOptions(eligible, currentServiceId);
};
